import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Layout, Menu, Spin } from '@arco-design/web-react';
import {
  IconDashboard,
  IconDownload,
  IconEmail,
  IconFile,
  IconList,
  IconMenuFold,
  IconMenuUnfold,
  IconMessage,
  IconSafe,
  IconUser,
  IconUserAdd,
  IconUserGroup,
} from '@arco-design/web-react/icon';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import ProtectedRoute from '@/components/ProtectedRoute';
import lazyload from '@/utils/lazyload';
import { appRoutes, useLandingPath, useVisibleMenuItems, PermissionRoute } from '@/routes';
import type { GlobalState } from '@/store';
import settings from '@/settings.json';
import styles from '@/style/layout.module.less';

const { Sider, Content } = Layout;
const MenuItem = Menu.Item;

const Dashboard = lazyload(() => import('./pages/dashboard'));
const Users = lazyload(() => import('./pages/users'));
const Roles = lazyload(() => import('./pages/roles'));
const News = lazyload(() => import('./pages/news'));
const NewsEditPage = lazyload(() => import('./features/news/components/NewsEditPage'));
const Profile = lazyload(() => import('./pages/profile'));
const Inquiries = lazyload(() => import('./pages/inquiries'));
const Downloads = lazyload(() => import('./pages/downloads'));
const Newsletters = lazyload(() => import('./pages/newsletters'));
const BdOwners = lazyload(() => import('./pages/bdOwners'));
const OperationLogs = lazyload(() => import('./pages/operationLogs'));
const Help = lazyload(() => import('./pages/help'));

const iconMap: Record<string, React.ReactNode> = {
  profile: <IconUser />,
  dashboard: <IconDashboard />,
  inquiries: <IconMessage />,
  downloads: <IconDownload />,
  newsletters: <IconEmail />,
  users: <IconUserGroup />,
  roles: <IconSafe />,
  news: <IconFile />,
  masterDataBd: <IconUserAdd />,
  operationLogs: <IconList />,
};

const routeComponents: Record<string, React.ComponentType> = {
  profile: Profile,
  dashboard: Dashboard,
  inquiries: Inquiries,
  downloads: Downloads,
  newsletters: Newsletters,
  users: Users,
  roles: Roles,
  news: News,
  newsCreate: NewsEditPage,
  newsEdit: NewsEditPage,
  masterDataBd: BdOwners,
  operationLogs: OperationLogs,
  help: Help,
};

function PageLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const collapsed = useSelector((state: GlobalState) => state.sidebarCollapsed);
  const userLoading = useSelector((state: GlobalState) => state.userLoading);
  const visibleMenuItems = useVisibleMenuItems();
  const landingPath = useLandingPath();
  const [selectedKey, setSelectedKey] = useState('');

  const showNavbar = settings.navbar;
  const showMenu = settings.menu;
  const showFooter = settings.footer;
  const navbarHeight = showNavbar ? 60 : 0;
  const menuWidth = collapsed ? 48 : settings.menuWidth;

  useEffect(() => {
    const matched = visibleMenuItems.find(
      (item) => item.path && (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)),
    );
    setSelectedKey(matched?.key ?? '');
  }, [location.pathname, visibleMenuItems]);

  // 动态浏览器标签页标题：按当前路由 key 翻译 + 品牌后缀，随语言切换刷新
  useEffect(() => {
    const route = appRoutes.find(
      (r) => location.pathname === r.path || location.pathname.startsWith(`${r.path}/`),
    );
    const titleKey = route ? `menu.${route.key}` : null;
    document.title = titleKey ? `${t(titleKey)} - LetsFly` : 'LetsFly';
  }, [location.pathname, t]);

  if (userLoading) {
    return (
      <div className={styles.spin}>
        <Spin size={32} />
      </div>
    );
  }

  return (
    <Layout className={styles.layout}>
      {showNavbar && (
        <div className={styles['layout-navbar']}>
          <NavBar />
        </div>
      )}
      <Layout>
        {showMenu && (
          <Sider
            className={styles['layout-sider']}
            width={menuWidth}
            collapsed={collapsed}
            collapsible
            trigger={null}
            breakpoint="xl"
          >
            <div className={styles['menu-wrapper']}>
              <Menu
                selectedKeys={[selectedKey]}
                onClickMenuItem={(key) => {
                  const item = visibleMenuItems.find((menu) => menu.key === key);
                  if (item?.path) {
                    navigate(item.path);
                  }
                }}
              >
                {visibleMenuItems.map((item) => (
                  <MenuItem key={item.key}>
                    {iconMap[item.key]}
                    {t(item.label)}
                  </MenuItem>
                ))}
              </Menu>
            </div>
            <button
              type="button"
              className={styles['collapse-btn']}
              onClick={() => dispatch({ type: 'toggle-sidebar' })}
            >
              {collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
            </button>
          </Sider>
        )}
        <Layout
          className={styles['layout-content']}
          style={{
            paddingLeft: showMenu ? menuWidth : 0,
            paddingTop: navbarHeight,
          }}
        >
          <div className={styles['layout-content-wrapper']}>
            <Content className={styles['content-public']}>
              <Routes>
                {appRoutes.map((route) => {
                  const Component = routeComponents[route.key];
                  if (!Component) {
                    return null;
                  }
                  return (
                    <Route
                      key={route.key}
                      path={route.path}
                      element={
                        <PermissionRoute permission={route.permission}>
                          <Component />
                        </PermissionRoute>
                      }
                    />
                  );
                })}
                <Route path="/" element={<Navigate to={landingPath} replace />} />
                <Route path="*" element={<Navigate to={landingPath} replace />} />
              </Routes>
            </Content>
          </div>
          {showFooter && <Footer />}
        </Layout>
      </Layout>
    </Layout>
  );
}

export default function AppLayout() {
  return (
    <ProtectedRoute>
      <PageLayout />
    </ProtectedRoute>
  );
}
